import { apiStatic } from 'AurionCR/components';
import { format } from 'date-fns';
import { sanitizeFileName, sanitizeTitle, stringToBase64 } from 'components/helpers';
import { API_MEDIA_UPLOADS } from 'services/media-uploads';
import {
  API_RATING_PROCESS_DOCUMENTATION,
  eDocumentType,
} from 'services/rating-process-documentation';
import { loadRatingProcessData, loadCurrentLanguage, loadTranslateFunction } from './load-data';
import { generatePdfHtml } from './generate-html';
import { TranslateFunction } from './types';

/**
 * Main function for generating and saving PDF
 * Completely independent of Redux store
 * @param ratingProcessID - ID of rating process
 * @param t - Optional translate function (if not provided, will be loaded automatically)
 * @returns Promise with filePath of the saved PDF
 */
export const generateAndSavePdf = async (
  ratingProcessID: number,
  t?: TranslateFunction,
): Promise<string> => {
  // Load rating process data
  const mainData = await loadRatingProcessData(ratingProcessID);

  // Load translate function if not provided
  let translateFunction = t;
  if (!translateFunction) {
    const languageID = await loadCurrentLanguage();
    translateFunction = await loadTranslateFunction(languageID);
  }

  // Generate PDF HTML
  const html = await generatePdfHtml(mainData, translateFunction);

  // Convert HTML to base64
  const base64EncodedHtml = stringToBase64(html);
  const formDataPdfGeneratorHelper = new FormData();
  formDataPdfGeneratorHelper.append('encodedHtml', base64EncodedHtml);

  // Convert HTML to PDF
  const { data }: { data: any } = await apiStatic.post(
    `PdfGeneratorHelper/GetPdfFromFile`,
    formDataPdfGeneratorHelper,
    {
      responseType: 'arraybuffer',
      headers: {
        Accept: 'application/pdf',
      },
    },
  );

  // Create PDF file
  let title = sanitizeTitle(
    `${mainData?.ratedEntityName || ''} ${mainData?.ratingProcessTypeTitle || ''} | ${format(
      new Date(),
      'yyyy MM',
    )}`,
  );
  if (mainData?.ratingProcessDocumentationsCount && mainData?.ratingProcessDocumentationsCount > 0) {
    title += ` v${mainData?.ratingProcessDocumentationsCount+1}`;
  }
  const pdfFile = new File(
    [new Blob([data], { type: 'application/pdf' })],
    sanitizeFileName(title) + '.pdf',
    {
      type: 'application/pdf',
    },
  );

  // Upload PDF to cloud
  const formDataPdf = new FormData();
  formDataPdf.append('file', pdfFile);
  const { data: filePath } = await apiStatic.post(
    API_MEDIA_UPLOADS.UPLOAD_FILE_TO_CLOUD,
    formDataPdf,
  );

  // Save documentation record
  await apiStatic.post(API_RATING_PROCESS_DOCUMENTATION.POST, {
    title,
    filePath,
    ratingProcessID: Number(ratingProcessID),
    documentType: eDocumentType.Auto,
  });

  return filePath;
};

